import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ChevronLeft, ChevronRight, Check } from 'lucide-react';

interface TourStep {
  title: string;
  description: string;
  element?: string; // CSS selector for highlighting
}

const tourSteps: TourStep[] = [
  {
    title: 'Welcome to CoreMonit!',
    description: 'This quick tour will help you navigate the platform and discover key features. You can exit at any time.',
  },
  {
    title: 'Super Dashboard',
    description: 'Your central hub for infrastructure health. View real-time metrics, alerts, and AI insights all in one place.',
    element: '[data-tour="dashboard"]',
  },
  {
    title: 'Monitoring Tools',
    description: 'Track logs, metrics, traces, uptime, and synthetic monitoring. Deep visibility into your infrastructure.',
    element: '[data-tour="monitoring"]',
  },
  {
    title: 'Server Management',
    description: 'Manage servers, networks, databases, containers, and services. Full infrastructure control.',
    element: '[data-tour="servers"]',
  },
  {
    title: 'AI Intelligence',
    description: 'Auto-healing, predictive analytics, and smart alerts powered by AI. Let COMO handle incidents automatically.',
    element: '[data-tour="ai"]',
  },
  {
    title: 'Microservices',
    description: 'Monitor APIs, websites, service mesh, and distributed tracing. Complete microservices observability.',
    element: '[data-tour="microservices"]',
  },
  {
    title: 'COMO AI Assistant',
    description: 'Click the floating button anytime to chat with COMO. Ask questions, get insights, or request actions.',
    element: '[data-tour="como"]',
  },
];

interface PlatformTourProps {
  onComplete: () => void;
  onSkip: () => void;
}

export function PlatformTour({ onComplete, onSkip }: PlatformTourProps) {
  const [currentStep, setCurrentStep] = useState(0);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const currentTourStep = tourSteps[currentStep];
  const progress = ((currentStep + 1) / tourSteps.length) * 100;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10065] flex items-center justify-center pointer-events-none"
      >
        {/* Spotlight Effect (optional - highlights elements) */}
        <div className="absolute inset-0 bg-dark-900/80 backdrop-blur-sm pointer-events-auto" onClick={onSkip} />

        {/* Tour Card */}
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          className="relative w-full max-w-2xl mx-4 pointer-events-auto"
        >
          {/* Glow Background */}
          <div className="absolute inset-0 bg-gradient-to-r from-cyber-cyan/20 via-cyber-blue/20 to-cyber-purple/20 blur-2xl" />

          {/* Card */}
          <div className="relative bg-dark-900 border border-cyber-cyan/30 rounded-2xl shadow-2xl overflow-hidden">
            {/* Progress Bar */}
            <div className="h-1 bg-dark-800">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
                className="h-full bg-gradient-to-r from-cyber-cyan via-cyber-blue to-cyber-purple"
              />
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-mono text-cyber-cyan">
                      {currentStep + 1} / {tourSteps.length}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    {currentTourStep.title}
                  </h3>
                  <p className="text-gray-300">
                    {currentTourStep.description}
                  </p>
                </div>
                <button
                  onClick={onSkip}
                  className="p-2 hover:bg-dark-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-between pt-4 border-t border-dark-800">
                <button
                  onClick={handlePrevious}
                  disabled={currentStep === 0}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>

                <div className="flex gap-1">
                  {tourSteps.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentStep(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentStep
                          ? 'bg-cyber-cyan w-8'
                          : index < currentStep
                          ? 'bg-cyber-cyan/50'
                          : 'bg-dark-700'
                      }`}
                      aria-label={`Go to step ${index + 1}`}
                    />
                  ))}
                </div>

                <button
                  onClick={handleNext}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-gradient-to-r from-cyber-cyan to-cyber-blue text-white rounded-lg hover:opacity-90 transition-opacity"
                >
                  {currentStep === tourSteps.length - 1 ? (
                    <>
                      <Check className="w-4 h-4" />
                      Finish
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
